import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react';
import { format, parseISO } from 'date-fns';
import type { SyncedBookingInfo } from '@/types/api';
import { ROW_HEIGHT, BAR_MARGIN } from '@/utils/timeline';

/** A single rendered booking bar: one contiguous group of table rows. */
export interface Bar {
  key: string;
  booking: SyncedBookingInfo;
  /** Contiguous indices into the visible (floor) table list, ascending. */
  tableIndices: number[];
  x: number;
  width: number;
  draggable: boolean;
}

const STATUS_CLASSES: Record<string, string> = {
  PENDING: 'bg-amber-200 border-amber-400 text-amber-900',
  CONFIRMED: 'bg-green-200 border-green-400 text-green-900',
  SEATED: 'bg-blue-200 border-blue-400 text-blue-900',
  COMPLETED: 'bg-gray-200 border-gray-400 text-gray-900',
  CANCELLED: 'bg-red-200 border-red-400 text-red-900',
  NO_SHOW: 'bg-red-200 border-red-400 text-red-900',
};

export const statusClasses = (status: string | null): string =>
  (status && STATUS_CLASSES[status]) || 'bg-gray-200 border-gray-400 text-gray-900';

const label = (b: SyncedBookingInfo): string => {
  const time = format(parseISO(b.startTime), 'HH:mm');
  const party = b.partySize ? `${b.partySize}p` : '';
  const name = b.customerName || 'Anonymous';
  return [time, party, name].filter(Boolean).join(' · ');
};

interface TimelineBarProps {
  bar: Bar;
  selected: boolean;
  /** True while this bar is the one being dragged/resized. */
  active: boolean;
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: (e: ReactPointerEvent) => void;
  onClick: (e: ReactMouseEvent) => void;
  onDoubleClick?: (e: ReactMouseEvent) => void;
}

export default function TimelineBar({
  bar,
  selected,
  active,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onClick,
  onDoubleClick,
}: TimelineBarProps) {
  const topIndex = bar.tableIndices[0];
  const count = bar.tableIndices.length;
  const top = topIndex * ROW_HEIGHT + BAR_MARGIN;
  const height = count * ROW_HEIGHT - BAR_MARGIN * 2;

  const showHandles = bar.draggable && bar.width > 28;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label(bar.booking)}
      onPointerDown={bar.draggable ? onPointerDown : undefined}
      onPointerMove={bar.draggable ? onPointerMove : undefined}
      onPointerUp={bar.draggable ? onPointerUp : undefined}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      title={
        bar.draggable
          ? undefined
          : 'This booking has no sync id yet and cannot be moved from the dashboard.'
      }
      className={`absolute rounded border px-1.5 py-0.5 text-xs overflow-hidden select-none ${statusClasses(
        bar.booking.status,
      )} ${selected ? 'ring-2 ring-primary-600 z-20' : 'z-10'} ${
        bar.draggable ? 'cursor-move' : 'opacity-60 cursor-default'
      } ${active ? 'shadow-lg opacity-90' : ''}`}
      style={{ left: bar.x, top, width: Math.max(bar.width - 1, 4), height }}
    >
      <div className="truncate font-medium pointer-events-none">{label(bar.booking)}</div>
      {bar.booking.notes && (
        <div className="truncate text-[11px] opacity-75 pointer-events-none">{bar.booking.notes}</div>
      )}

      {showHandles && (
        <>
          {/* horizontal (duration) handles */}
          <span className="absolute inset-y-0 left-0 w-2 cursor-ew-resize" data-handle="start" />
          <span className="absolute inset-y-0 right-0 w-2 cursor-ew-resize" data-handle="end" />
          {/* vertical (add/remove tables) handles */}
          <span className="absolute inset-x-0 top-0 h-2 cursor-ns-resize" data-handle="top" />
          <span className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize" data-handle="bottom" />
        </>
      )}
    </div>
  );
}
